import { DIGEST_HOURS_UTC, NOTIFICATION_DELIVERIES } from "@ondo/shared";
import type { NotificationDelivery, NotificationType } from "@ondo/shared";

/**
 * Every word on the notification preferences screen.
 *
 * WHY THE LABELS ARE HERE AND NOT IN `@ondo/shared`. The API answers with a
 * kind and a delivery and no copy at all, for the reason `NotificationTarget`
 * carries no `href`: a response that shipped the product's wording would be a
 * second authority on it. So the sentences live beside the screen that says
 * them.
 */

/**
 * The three choices, in the order the ticket names them.
 *
 * `as const` rather than annotated `NotificationDelivery`, which is not a style
 * choice: the annotation would widen `value` to the whole union and make the
 * check below vacuous. Kept literal, the two lines after this are a real one.
 */
export const DELIVERY_OPTIONS = [
  {
    value: "immediate",
    label: "Immediately",
    hint: "As it happens.",
  },
  {
    value: "digest",
    label: "In the daily digest",
    hint: "Held, and sent once a day with everything else you have chosen to batch.",
  },
  {
    value: "off",
    label: "Off",
    hint: "Not sent at all.",
  },
] as const satisfies readonly { value: NotificationDelivery; label: string; hint: string }[];

/**
 * A MODE WITH NO CONTROL IS A COMPILE ERROR, which is the silent half of a
 * half-shipped feature and the one thing this screen could get wrong without
 * anything failing. A fourth value added to `ondo.notification_delivery` and to
 * `NOTIFICATION_DELIVERIES` would otherwise be a choice nobody can make and a
 * stored value the screen renders as nothing checked at all.
 *
 * `satisfies` above proves every option IS a delivery; this proves every
 * delivery HAS an option. Neither direction implies the other.
 */
type Offered = (typeof DELIVERY_OPTIONS)[number]["value"];
const _EVERY_DELIVERY_HAS_A_CONTROL: Record<NotificationDelivery, true> & {
  [K in Offered]: true;
} = Object.fromEntries(
  NOTIFICATION_DELIVERIES.map((delivery) => [delivery, true]),
) as Record<Offered, true>;
void _EVERY_DELIVERY_HAS_A_CONTROL;

/** The sections, in the order they are read. */
export const PREFERENCE_GROUPS = [
  {
    id: "money",
    title: "Money and title",
    // Named first, and said plainly: these are the ones where not being told
    // costs something. The ticket is explicit that they may still be switched
    // off — "there is no mandatory notification" — so this is a warning and
    // never a refusal.
    blurb:
      "Who owns a work, and money you are owed or have been paid. You can switch any of these off; what you would stop being told is written under each one.",
  },
  {
    id: "verifications",
    title: "Verifications",
    blurb: "Questions about whether something on a record is true.",
  },
  {
    id: "consignments",
    title: "Consignments",
    blurb: "Works placed with a gallery, and what happens to those arrangements.",
  },
  {
    id: "loans",
    // Its own section rather than a line inside "Consignments", because these
    // group by SUBJECT and a loan is a different subject: a consignment is a
    // work placed to be sold, a loan is a work lent and coming back. The two
    // have different parties and a reader who cares about one may not care
    // about the other, which is the whole point of grouping this way.
    title: "Loans",
    blurb: "Works lent out or borrowed, and what happens to those arrangements.",
  },
  {
    id: "agents",
    title: "Agents and representation",
    blurb: "Who may act for a work, and requests to.",
  },
  {
    id: "records",
    title: "Collections and works",
    blurb: "Things added to what you hold, and access given to you.",
  },
  {
    id: "affiliations",
    title: "Affiliations",
    blurb: "People and firms claiming a connection to you.",
  },
] as const;

export type PreferenceGroupId = (typeof PREFERENCE_GROUPS)[number]["id"];

/**
 * Which section each kind sits in.
 *
 * TOTAL OVER `NotificationType`, so a new verb is a compile error here rather
 * than a kind that silently appears in no section and can never be configured.
 * `TAB_OF` in the inbox's constants is the same device for the same reason.
 *
 * It groups by SUBJECT, where the inbox groups by what a row wants from the
 * reader. The two screens ask different questions: an inbox reader is deciding
 * what to do next, and somebody setting preferences is deciding how much they
 * care about a topic. "Tell me about money at once and registrations once a
 * day" is a sentence about subjects.
 */
export const GROUP_OF: Record<NotificationType, PreferenceGroupId> = {
  "verification.requested": "verifications",
  "verification.referred": "verifications",
  "verification.answered": "verifications",
  "verification.attested": "verifications",
  // "verifications" ON ALL THREE, including the assignment — and the assignment
  // is the one worth arguing. It is the only row in this map addressed to
  // somebody doing their JOB rather than hearing about their own holdings, which
  // makes a "work" group tempting. But this map groups by SUBJECT, and the
  // subject is a confirmation: somebody who turned verification news down has
  // said something about confirmations, and a studio reviewer who did it would
  // mean it about this too.
  //
  // NOT "money", on any of the three, even though a case ends in a fee. None of
  // these three rows is about the fee — the fee is raised by the ANSWER, which
  // is `verification.answered`, and nothing in this feature notifies about money
  // at all.
  "verification.evidence_requested": "verifications",
  "verification.extended": "verifications",
  "verification.assigned": "verifications",
  // "verifications" and not "records", because this map groups by SUBJECT and
  // the subject is a confirmation flow: the row exists because somebody asked a
  // party to confirm an authorship claim. A reader who turns verification news
  // down has said something about confirmations, which is what this is.
  "attribution.claimed": "verifications",
  "affiliation.claimed": "affiliations",
  "affiliation.answered": "affiliations",
  "work.registered": "records",
  "collection.shared": "records",
  "agent.assigned": "agents",
  "agent.removed": "agents",
  // A term running out is news about an APPOINTMENT, so it groups with the
  // appointment rather than with money -- even though what is at stake is a
  // commission. A reader who turned agent news down has said something about
  // agents; they have not said anything about their own money.
  "mandate.expiring": "agents",
  "mandate.expired": "agents",
  // THE AGENTS SECTION AND NOT "affiliations", which is the grouping these three
  // look like they want: what moved is somebody's membership of an organisation,
  // and there is a section for exactly that. It is the wrong one. `affiliations`
  // is "people and firms claiming a connection to YOU" — rows addressed to the
  // subject of a claim — and these are addressed to a CLIENT about an agent they
  // appointed. The subject is the appointment, which is what the reader would be
  // deciding about when they turn this down.
  //
  // The same test `mandate.expiring` applies one entry up: a reader who turned
  // agent news down has said something about agents.
  "agent.organisation_joined": "agents",
  "agent.organisation_left": "agents",
  "agent.authority_changed": "agents",
  "representation.requested": "agents",
  "representation.answered": "agents",
  "consignment.offered": "consignments",
  "consignment.answered": "consignments",
  "consignment.ended": "consignments",
  "consignment.revision_proposed": "consignments",
  "consignment.revision_answered": "consignments",
  // AN OFFER GROUPS WITH CONSIGNMENTS AND NOT WITH MONEY, although what it
  // carries is a price. The subject is the ARRANGEMENT: an offer arrives because
  // a work is consigned, it is answered on the consignment's own screen, and a
  // reader who turned consignment news down has said something about the
  // arrangements they are party to. `money` is where a SALE's consequences sit —
  // title, settlements, a covenant falling due — and an offer is not one yet.
  //
  // `offer.refused` is here with the other two even though nobody acted on it,
  // for the reason `mandate.expiring` is grouped with agents: the subject is
  // what the news is ABOUT, not who caused it.
  "offer.received": "consignments",
  "offer.refused": "consignments",
  "offer.answered": "consignments",
  "loan.proposed": "loans",
  "loan.answered": "loans",
  "loan.ended": "loans",
  "transfer.offered": "money",
  "transfer.answered": "money",
  "transfer.withdrawn": "money",
  "sale.reversed": "money",
  // A MONEY ROW, beside the transfer verbs, because it is a fact about one of
  // the reader's own sales. A seller who has muted sales has muted their sales.
  "claim.disputed": "money",
  // A MONEY ROW, because what hangs off a sale's history line is its invoice.
  // The three sit beside the transfer verbs rather than with the record ones
  // for that reason: a reader who has muted sales has muted the paperwork of
  // their sales, which is what they meant.
  "sale.addition_proposed": "money",
  "sale.addition_answered": "money",
  "sale.addition_approved": "money",
  "settlement.due": "money",
  "settlement.remitted": "money",
  "settlement.answered": "money",
};

/**
 * What each kind is called on this screen.
 *
 * A SECOND MAP, shadowing `TITLES` in the inbox's constants, and the reason is
 * the one `profile.constants.ts` gives about `ORG_TYPE_TAGS`: those titles are
 * written as news that has already happened — "A work has been sold to you" —
 * and a settings row is a standing thing you are choosing about, which reads as
 * a noun phrase. Kept total so a new verb is a compile error in both.
 */
export const PREFERENCE_LABELS: Record<NotificationType, string> = {
  "verification.requested": "Somebody asks you to verify something",
  "verification.referred": "A verification request is passed on to you",
  "verification.answered": "Your verification request is answered",
  "verification.attested": "Somebody takes a position on an entry you wrote",
  // Noun phrases in the present tense, as this map's header requires: these are
  // standing things being chosen about rather than news that has happened.
  "verification.evidence_requested": "A studio asks for more evidence before answering",
  "verification.extended": "A studio revises its target date for answering",
  "verification.assigned": "A confirmation request is assigned to you",
  "attribution.claimed": "A work is attributed to an artist you are appointed for",
  "affiliation.claimed": "Somebody claims an affiliation with you",
  "affiliation.answered": "Your affiliation claim is answered",
  "work.registered": "A work is registered into your collection",
  "collection.shared": "A collection is shared with you",
  "agent.assigned": "You are named as the agent for a work",
  "agent.removed": "You are no longer the agent for a work",
  // A NOUN PHRASE ABOUT A STANDING CHOICE, per this map's own header, and
  // phrased from the reader's side rather than the sender's. Both parties to an
  // appointment receive these, so neither "your agent's" nor "your client's"
  // would be true of the same row twice.
  "mandate.expiring": "An agent appointment is about to run out",
  "mandate.expired": "An agent appointment runs out",
  // A NOUN PHRASE ABOUT A STANDING CHOICE, per this map's own header, and
  // phrased from the reader's side — which here is unambiguous in a way the two
  // rows above are not. Only one party ever receives these: the person who made
  // the appointment. So "an agent you appointed" is true of every row, and the
  // hedging `mandate.expiring` needs does not apply.
  "agent.organisation_joined": "An agent you appointed joins an organisation",
  // "leaves or is suspended from", spelled out, because one setting governs
  // both — 20261004092000 decision 8 split the verbs on the access consequence
  // and not on the status word, and a settings row saying only "leaves" would
  // understate what the reader is switching off.
  "agent.organisation_left":
    "An agent you appointed leaves or is suspended from an organisation",
  "agent.authority_changed":
    "Somebody else changes what one of your appointments reaches",
  "representation.requested": "An agent asks to represent one of your works",
  "representation.answered": "Your representation request is answered",
  "consignment.offered": "A work is offered to your gallery",
  "consignment.answered": "Your consignment is answered",
  "consignment.ended": "A consignment ends",
  "consignment.revision_proposed": "New terms are proposed on a consignment",
  "consignment.revision_answered": "Terms you proposed are answered",
  // PHRASED FROM THE READER'S SIDE, which on these three is harder than it
  // looks: each goes to more than one party. `received` reaches the owner and
  // the gallery, `refused` reaches those two and the buyer, and `answered`
  // reaches the buyer and the gallery — so none of them may say "your" of a
  // role, and the second says what happened rather than who it happened to.
  "offer.received": "A price is offered for a work you are party to",
  "offer.refused": "An offer is refused by the agreed floor, with nobody asked",
  "offer.answered": "An offer is answered by the work's owner",
  "loan.proposed": "A work is proposed to you on loan",
  "loan.answered": "A loan you proposed is answered",
  "loan.ended": "A loan ends",
  "transfer.offered": "A work is sold to you and waits on your answer",
  "transfer.answered": "A buyer answers a sale you effected",
  "transfer.withdrawn": "A sale recorded against you is withdrawn",
  "sale.reversed": "A sale you recorded could not be completed",
  "claim.disputed": "A buyer recorded off ONDO disputes one of your sales",
  "sale.addition_proposed": "A sale's record has an addition awaiting your approval",
  "sale.addition_answered": "An addition you proposed to a sale's record is refused",
  "sale.addition_approved": "An addition you proposed becomes part of a sale's record",
  "settlement.due": "A resale settlement falls due under one of your covenants",
  "settlement.remitted": "A gallery records paying you a resale share",
  "settlement.answered": "An artist answers a settlement you recorded",
};

/**
 * What a person stops being told, said in one line, when they switch a money or
 * title notification off.
 *
 * THE TICKET'S ONE SUGGESTION, and it was explicitly not a requirement:
 * "when a money or title notification is switched off, say in a line what the
 * person stops being told. Not a refusal — just so the risk is taken knowingly,
 * which is what makes it fair to place it on them."
 *
 * So each line names the consequence AND where the fact still lives, because
 * the fact does still live somewhere: a preference governs what you are told,
 * never what is recorded. Partial on purpose — a line under every row would be
 * noise, and these are the rows where silence costs something.
 *
 * Shown when the row is set to off, and not when it is batched: a digest is
 * still being told.
 */
export const SILENCED: Partial<Record<NotificationType, string>> = {
  // NEITHER MONEY NOR TITLE EITHER, and the consequence is the sharpest on this
  // map: a claim your artist did not make, confirmed by somebody who was
  // willing to confirm it, with you never told it was asked. OND-77's own
  // example is three misattributions among nine works. The fact still lives in
  // the catalogue, which is where it lived before this notification existed and
  // which you would have to remember to open.
  "attribution.claimed":
    "You will not be told when somebody asks another party to confirm that a work is by an artist you are appointed for. The claim and whatever was answered about it are on your catalogue, where you would have to go and look.",
  // THREE ROWS THAT ARE NEITHER MONEY NOR TITLE, and they are here because
  // OND-107 asks for precisely what this map does: "A client who silenced this
  // kind is not told, and that is their risk to carry." A consequence line is
  // how a risk gets carried knowingly rather than discovered.
  //
  // Each names the consequence AND where the fact still lives, which is this
  // map's rule — and on all three the fact lives in the same place, because the
  // notification never carried it: the appointment is on the work's own record,
  // with the organisation it reaches and what it permits.
  "agent.organisation_joined":
    "You will not be told when an agent you appointed is recorded at a new organisation. Nothing about your appointment changes either way and the new organisation gets no access — but you would have to open the work to notice the agent's situation had moved at all.",
  // THE HEAVIEST OF THE THREE, and the sentence is the one OND-83 built the
  // feature around: a named organisation keeps what it was given after the
  // person who took it there has gone. Saying so is the whole reason the
  // notification exists.
  "agent.organisation_left":
    "You will not be told when an agent you appointed leaves or is suspended from an organisation. Your appointment stands exactly as recorded, which is the point: if you named that organisation, it keeps the access you gave it after the person who took it there has gone, and nothing will prompt you to look.",
  "agent.authority_changed":
    "You will not be told when a gallery or another agent changes what one of your appointments reaches. The change is recorded with who made it, on the work's own record, and it takes effect whether or not you read about it.",
  // A PROPOSAL TO CHANGE A PRICE IS A MONEY ROW, which is why it is in a map
  // the rest of whose members are sales and settlements. The consequence is the
  // mildest on this list and is still worth saying: nothing moves without your
  // acceptance, so silence costs you the chance to agree rather than the
  // agreement itself.
  "consignment.revision_proposed":
    "You will not be told when the other party proposes a new asking price or floor. Nothing changes until you accept it, so the arrangement simply carries on at the terms you have — you would have to open the work to find the proposal.",
  "transfer.offered":
    "You will not be told when somebody records selling a work to you. Title does not move until you accept it, so an offer nobody answers simply stays open — you would have to find it on Offered to you.",
  "transfer.answered":
    "You will not be told whether a buyer accepted or declined a sale you recorded. The sale stays in your ledger either way, awaiting an answer you will not hear about.",
  "transfer.withdrawn":
    "You will not be told when somebody retracts a sale they recorded to you. This is the last notice about that work you can be shown, because the record closes with it.",
  "sale.reversed":
    "You will not be told when ONDO reverses a sale it could not complete. The work stays where it was and nothing is lost, but you would have to notice the sale is gone and record it again.",
  // THE MOST EXPENSIVE SILENCE ON THIS LIST AFTER THE ADDITION, and for the
  // opposite reason: nothing is blocked, so nothing ever surfaces. A disputed
  // sale looks exactly like an unanswered one from every screen in the product.
  "claim.disputed":
    "You will not be told when a buyer you recorded by email says they did not buy the work. The sale stands either way and the work does not come back to you — but the dispute sits on the work's record, and you would have to open it to find out it is there.",
  // THE HEAVIEST CONSEQUENCE ON THIS LIST, and it is worth saying plainly: an
  // addition needs EVERY party, so one reader who never answers is one reader
  // who stops the record being corrected. Nothing is added without them, which
  // is the protection — and it is also what makes the silence expensive.
  "sale.addition_proposed":
    "You will not be told when the selling firm asks to add documents or a correction to a sale's record. Nothing is added until every party to the sale approves, so the proposal simply waits — you would have to open the work's record to find it.",
  "sale.addition_answered":
    "You will not be told when a party to the sale refuses an addition you proposed. The record says what it said before, and your proposal is closed until you make another.",
  "sale.addition_approved":
    "You will not be told when the last party approves an addition you proposed. It is part of the record either way; you would have to open it to see that it landed.",
  "settlement.due":
    "You will not be told when a resale triggers one of your covenants. You are not a party to that sale, so a notification is the only way you learn of it at all — otherwise you would have to open Settlements and look.",
  "settlement.remitted":
    "You will not be told when a gallery records paying you a resale share, and that is the notice that invites you to confirm or dispute it. The settlement stands as recorded until you answer it.",
  "settlement.answered":
    "You will not be told whether an artist confirmed or disputed a settlement you recorded. A dispute would sit unanswered on the record until you looked.",
};

export const PREFERENCES_COPY = {
  loading: "Loading your notification settings…",
  lead: "Choose how each kind of notification reaches you. Everything is on unless you change it.",
  firmLead:
    "Choose how each kind of notification reaches this organisation. Everything is on unless you change it.",
  // The screen is uniform, and the ticket's answer is why — said once, at the
  // top, rather than implied by the absence of a locked row.
  risk:
    "Anything here can be switched off, including money and transfers of title. Nothing is sent that you have switched off, and the consequence of not being told is yours.",
  readOnlyNote:
    "You can see this organisation's settings because you hold a seat here. Changing them is an act of the organisation, so an administrator has to do it.",
  saving: "Saving…",
  saved: "Saved.",
  unknownError: "That could not be saved. Try again.",
  loadError: "Those settings could not be loaded.",
  groupLegend: (title: string) => `${title} — how each of these reaches you`,
  rowLegend: (label: string) => `${label} — how this reaches you`,
} as const;

/**
 * When the daily digest is sent, as the twenty-four choices a reader sees.
 *
 * EVERY LABEL SAYS UTC, and that is the owner's instruction rather than a
 * preference of this file's: "The person's editable send time is also expressed
 * in UTC; label it clearly. This is one global clock, not 09:00 in each reader's
 * local timezone." A control reading "09:00" would be read as nine o'clock where
 * the reader is standing, which is the one thing it is not.
 *
 * Derived from `DIGEST_HOURS_UTC` rather than written out, so the list of hours
 * exists once — in `packages/shared`, beside the default, where the Zod schema
 * at the door reads it too.
 */
export const DIGEST_HOUR_OPTIONS = DIGEST_HOURS_UTC.map((hour) => ({
  value: hour,
  label: `${String(hour).padStart(2, "0")}:00 UTC`,
}));

/** Every word of the digest control. */
export const DIGEST_COPY = {
  title: "The daily digest",
  blurb:
    "Anything you have set to arrive in the daily digest is held and sent once a day, in one email.",
  label: "Sent at",
  // THE DEFAULT IS NAMED RATHER THAN IMPLIED, because a control already showing
  // 09:00 cannot say whether that is a choice or the absence of one — and the
  // two differ: somebody who has not chosen follows the product's default if it
  // ever moves.
  unchosenNote: "You have not chosen a time, so the digest arrives at 09:00 UTC.",
  chosenNote: (label: string) => `Your digest is sent at ${label}.`,
  firmBlurb:
    "Anything this organisation has set to arrive in the daily digest is held and sent once a day, to everybody holding a seat here.",
  // THE ONE THING THE CONTROL IS NOT. OND-96's digest is what `In the daily
  // digest` means on a kind; there is no switch here that stops it, because
  // stopping it is switching those kinds back to immediate or to off.
  noOffNote:
    "There is no way to turn the digest off here: it carries whatever you have set to arrive in it, so emptying it is a matter of the choices below.",
  utcNote:
    "Times are UTC for everybody, not your local time — one clock, so a digest is the same batch wherever it is read.",
  unknownError: "That time could not be saved. Try again.",
} as const;
